import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, BookmarkCheck, Languages } from "lucide-react";
import { useLang } from "../lib/i18n";

export const Nav = ({ onNavigate, shortlistCount = 0 }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, lang, setLang } = useLang();

  const LINKS = [
    { label: t("nav.matcher"), id: "matcher" },
    { label: t("nav.library"), id: "library" },
    { label: t("nav.method"), id: "method" },
    { label: t("nav.handover"), id: "handover" }
  ];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (id) => { setOpen(false); onNavigate(id); };

  const langBtn = (
    <button data-testid="nav-lang-toggle" onClick={() => setLang(lang === "ar" ? "en" : "ar")}
      aria-label="Switch language"
      className="flex items-center gap-2 text-cream/70 hover:text-gold text-xs tracking-[0.2em] uppercase transition-colors duration-300">
      <Languages size={14} className="text-gold" />
      {lang === "ar" ? "EN" : "عربي"}
    </button>
  );

  return (
    <header data-testid="main-nav" className={`fixed top-0 left-0 right-0 z-40 border-b transition-[background-color,border-color] duration-500 ${scrolled ? "bg-ink/90 backdrop-blur-xl border-gold/25" : "bg-transparent border-transparent"}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <button data-testid="nav-brand" onClick={() => go("top")} className="flex items-center gap-3 group">
          <span className="w-9 h-9 grid place-items-center bg-gold text-ink font-display text-lg leading-none">A</span>
          <span className="text-left leading-tight">
            <span className="block font-display text-cream text-sm tracking-[0.25em]">AAYAT AI</span>
            <span className="block text-gold/80 text-[10px] tracking-[0.35em] uppercase">{t("nav.studio")}</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => go(l.id)}
              className="text-cream/70 hover:text-gold text-xs tracking-[0.25em] uppercase transition-colors duration-300">
              {l.label}
            </button>
          ))}
          <a data-testid="nav-catalogue-link" href="/AAYAT_Concept_Catalogue.pdf" target="_blank" rel="noopener noreferrer"
            className="text-gold/90 hover:text-cream text-xs tracking-[0.25em] uppercase underline underline-offset-4 decoration-gold/40 transition-colors duration-300">
            {t("nav.catalogue")}
          </a>
          {shortlistCount > 0 && (
            <button data-testid="nav-shortlist-count" onClick={() => go("contact")}
              className="flex items-center gap-2 border border-gold/50 text-gold text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:bg-gold/10 transition-colors duration-300">
              <BookmarkCheck size={13} />
              {t("nav.shortlist")} ({shortlistCount})
            </button>
          )}
          {langBtn}
          <button data-testid="nav-cta" onClick={() => go("contact")}
            className="group flex items-center gap-2 bg-gold text-ink text-xs tracking-[0.2em] uppercase px-5 py-3 hover:bg-cream transition-colors duration-300">
            {t("nav.cta")}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-4">
          {langBtn}
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(!open)} className="text-cream p-2" aria-label="Toggle menu" aria-expanded={open}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-gold/20"
            aria-label="Mobile">
            <div className="px-6 py-6 flex flex-col gap-5 items-start">
              {LINKS.map((l) => (
                <button key={l.id} data-testid={`nav-mobile-${l.id}`} onClick={() => go(l.id)}
                  className="text-left text-cream/80 font-display tracking-[0.2em] text-lg">{l.label}</button>
              ))}
              <button data-testid="nav-mobile-cta" onClick={() => go("contact")}
                className="mt-2 bg-gold text-ink text-xs tracking-[0.2em] uppercase px-5 py-4 text-left">
                {t("nav.cta")}
              </button>
              <a data-testid="nav-mobile-catalogue" href="/AAYAT_Concept_Catalogue.pdf" target="_blank" rel="noopener noreferrer"
                className="text-gold text-xs tracking-[0.25em] uppercase underline underline-offset-4 decoration-gold/40">
                {t("footer.catalogue")}
              </a>
              {shortlistCount > 0 && (
                <button data-testid="nav-mobile-shortlist" onClick={() => go("contact")}
                  className="flex items-center gap-2 border border-gold/50 text-gold text-xs tracking-[0.2em] uppercase px-5 py-3.5">
                  <BookmarkCheck size={13} />
                  {t("nav.shortlist")} ({shortlistCount})
                </button>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
